// Change orders: starting one on a locked job, approving it into the next revision (and
// everything that follows for the cards and for purchasing), declining it, and the two
// things a job can carry that are not change orders: a rework card and a warranty fix.

import {
  changesOnCard, generateCards, planCardsAfterChange, purchasingAfterChange, revisionDelta,
  type CardPlan, type Library, type PurchasingChange, type Resolution, type ResolutionLike, type RevisionDelta,
} from '@zerogap/engine';
import { deleteRow, fetchRows, insertRow, insertRows, refresh, rpc, updateRow, type Row } from './api';
import type { JobData } from './job';

export interface StoredChanges {
  items?: RevisionDelta['items'];
  materials?: RevisionDelta['materials'];
  purchasing?: PurchasingChange[];
  cards?: { removed: number; created: number; flagged: number; rework: CardPlan['rework'] };
}

/** The prior revision as the engine compares it: the resolution stored when it was locked. */
export function priorOf(resolution: Row | undefined): ResolutionLike | null {
  if (!resolution) return null;
  return { items: resolution.items ?? [], groups: resolution.chain ?? [], totals: resolution.totals ?? { price_cents: 0, labor_minutes: '0' } };
}

/** Start a change order: keep the job's rows as they stand, then open the job for editing. */
export async function startChangeOrder(data: JobData, lastRevision: Row, reason: string, description: string, existing: Row[]): Promise<Row> {
  const job = data.job!;
  const co = await insertRow('change_order', {
    job_id: job.id,
    number: existing.reduce((n, x) => Math.max(n, Number(x.number)), 0) + 1,
    reason: reason || null,
    description: description || null,
    from_revision_id: lastRevision.id,
    before: {
      job: { locked_at: job.locked_at, standards_version_id: job.standards_version_id, pricing_overrides: job.pricing_overrides ?? {} },
      job_item: data.items,
      job_standard: data.jobStandards,
      sub_line: data.subLines,
      decision_line: data.decisionLines,
      job_cost_line: data.costLines,
      room: data.rooms,
      room_standard: data.roomStandards,
      job_route_line: data.routeLines,
      job_bom_line: data.bomLines,
    },
  });
  await updateRow('job', job.id, { locked_at: null });
  return co;
}

const cardStatus = (c: Row, doneState: string): 'unpulled' | 'in_flight' | 'done' =>
  c.closed_at || c.state === doneState ? 'done' : c.pulled_at || c.person_id ? 'in_flight' : 'unpulled';

/**
 * Approve: the job as it stands becomes the next revision, and the consequences follow.
 * Returns what was done, in the words the screen shows.
 */
export async function approveChangeOrder(args: {
  co: Row;
  data: JobData;
  res: Resolution;
  lib: Library;
  prior: ResolutionLike;
  revNo: number;
  userId: string | null;
  boardStates: string[];
  card: { capHours: number; floorHours: number };
  /** material id → how much of it is on open orders for this job. */
  onOpenOrders: Record<string, string>;
  reasonLabel: string;
}): Promise<StoredChanges> {
  const { co, data, res, lib, prior, revNo, userId } = args;
  const job = data.job!;
  const delta = revisionDelta(prior, res);
  const purchasing = purchasingAfterChange(delta.materials, args.onOpenOrders);

  // 1. the next revision
  const dollars = (delta.price_delta_cents / 100).toFixed(2);
  const changeList = [
    `Change order ${co.number}${args.reasonLabel ? ` · ${args.reasonLabel}` : ''}${co.description ? ` · ${co.description}` : ''}`,
    `Price ${delta.price_delta_cents === 0 ? 'unchanged' : `${delta.price_delta_cents > 0 ? 'up' : 'down'} $${Math.abs(Number(dollars)).toFixed(2)}`}`,
    `Hours ${Number(delta.hours_delta) === 0 ? 'unchanged' : `${Number(delta.hours_delta) > 0 ? 'up' : 'down'} ${Math.abs(Number(delta.hours_delta)).toFixed(2)}`}`,
    ...delta.items.filter((i) => i.kind !== 'repriced').slice(0, 40).map((i) => (i.kind === 'changed' ? `${i.number}: ${i.what.join('; ')}` : `${i.number} ${i.kind}`)),
  ];
  const resolution = await insertRow('resolution', { job_id: job.id, standards_version_id: res.standards_version_id, items: res.items, totals: res.totals, chain: res.groups });
  const revision = await insertRow('revision', { job_id: job.id, rev_no: revNo, resolution_id: resolution.id, change_list: changeList, locked_by: userId });
  for (const v of data.views) if (!v.revision_id) await updateRow('view', v.id, { revision_id: revision.id });
  await updateRow('job', job.id, { locked_at: new Date().toISOString(), standards_version_id: res.standards_version_id });

  // 2. the cards: only when the job already has cards on the board
  const cards = await fetchRows('work_card', { eq: { job_id: job.id }, order: ['number'] });
  const done = args.boardStates[args.boardStates.length - 1] ?? 'Done';
  const queued = args.boardStates[0] ?? 'Queued';
  const summary = { removed: 0, created: 0, flagged: 0, rework: [] as CardPlan['rework'] };
  if (cards.length > 0) {
    const generated = generateCards(res, lib, args.card);
    const plan = planCardsAfterChange(
      cards.filter((c) => c.origin === 'generated' || !c.origin).map((c) => ({ id: c.id, number: Number(c.number), name: c.name, status: cardStatus(c, done), item_ids: (c.items as string[]) ?? [] })),
      generated,
      delta,
    );
    for (const id of plan.remove) {
      for (const l of await fetchRows('label', { eq: { kind: 'card', target_id: id } })) await deleteRow('label', l.id);
      await deleteRow('work_card', id);
    }
    let number = cards.filter((c) => !plan.remove.includes(c.id)).reduce((n, c) => Math.max(n, Number(c.number)), 0);
    for (const g of plan.create) {
      number += 1;
      const card = await insertRow('work_card', { job_id: job.id, number, name: g.name, phase_id: g.phase_id, break_label: g.break_label, state: queued, sort: number, hours_standard: g.hours, items: g.item_ids, bom: g.bom });
      await insertRows('work_card_step', g.steps.map((x, k) => ({ work_card_id: card.id, sort: k + 1, operation_id: x.operation_id, work_center_id: x.work_center_id, name: x.operation_name, qty: x.qty, unit: x.unit, minutes_standard: x.minutes })));
      await insertRow('label', { code: `ZG-C-${String(card.id).slice(0, 8).toUpperCase()}`, kind: 'card', target_id: card.id });
      const changes = changesOnCard(g, delta);
      if (changes.length > 0) {
        await insertRow('card_change_flag', { work_card_id: card.id, change_order_id: co.id, rev_no: revNo, changes });
        summary.flagged += 1;
      }
    }
    for (const f of plan.flag) await insertRow('card_change_flag', { work_card_id: f.card_id, change_order_id: co.id, rev_no: revNo, changes: f.changes });
    summary.removed = plan.remove.length;
    summary.created = plan.create.length;
    summary.flagged += plan.flag.length;
    summary.rework = plan.rework;
  }

  // 3. the change order itself, with what it did kept for the record
  const changes: StoredChanges = { items: delta.items, materials: delta.materials, purchasing, cards: summary };
  await updateRow('change_order', co.id, {
    state: 'approved', revision_id: revision.id, price_delta_cents: delta.price_delta_cents, hours_delta: delta.hours_delta, changes,
    decided_at: new Date().toISOString(), decided_by: userId,
  });
  refresh('work_card', 'work_card_step', 'label', 'card_change_flag', 'revision', 'resolution', 'view', 'job');
  return changes;
}

/** Decline: the job goes back to the rows kept when the change order was started, locked at the revision it had. */
export async function declineChangeOrder(co: Row, userId: string | null): Promise<void> {
  await rpc('decline_change_order', { p_change_order_id: co.id, p_user_id: userId });
  refresh('change_order', 'job', 'job_item', 'job_standard', 'sub_line', 'decision_line', 'job_cost_line', 'room', 'room_standard', 'job_route_line', 'job_bom_line', 'mark');
}

/** A card that is not generated from the route: rework after a change, or a warranty fix (always no charge). */
export async function addManualCard(jobId: string, origin: 'rework' | 'warranty', what: string, queuedState: string): Promise<Row> {
  const cards = await fetchRows('work_card', { eq: { job_id: jobId } });
  const number = cards.reduce((n, c) => Math.max(n, Number(c.number)), 0) + 1;
  const card = await insertRow('work_card', {
    job_id: jobId, number, name: `${origin === 'warranty' ? 'Warranty fix' : 'Rework'} · ${what}`, state: queuedState, sort: number, hours_standard: 0,
    items: [], bom: [], origin, non_billable: origin === 'warranty', reason: what,
  });
  await insertRow('label', { code: `ZG-C-${String(card.id).slice(0, 8).toUpperCase()}`, kind: 'card', target_id: card.id });
  return card;
}
